import * as React from "react"
import { Container, Row, Col, Spinner, Button, Form } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import ClinicSearchOptions from "../../../api/Clinics/Models/ClinicSearchOptions"
import { ClinicSearchOptionsGetObservable, ClinicSearchOptionsSetObservable, ClinicSearchPriceGetObservable, ClinicSearchPriceSetObservable } from "../../../api/Clinics/ClinicOptionsGet"
import ClinicSearchPrice from "../../../api/Clinics/Models/ClinicSearchPrice"
import ActionButton from "../../ActionButton"
import DraggableItemData from "../../Draggable/DraggableItemData"
import DroppableContainer from "../../Draggable/DroppableContainer"
import AdditionalOptions from "./AdditionalOptions"
import Checkbox from "../../Checkbox"

interface DefaultOptionsProps {
  onCancel: () => void
  isPortal: boolean
}

interface DefaultOptionsState {
  options?: ClinicSearchOptions
  prices?: ClinicSearchPrice[]
  radius1?: number
  radius2?: number
  mpSitesOnly: boolean
  mpSitesOnlyPortalAdmin?: boolean
  loadingOptions: boolean
  loadingPrice: boolean
  savingOptions: boolean
  savingPrice: boolean
  savingAdditionalOptions: boolean
  updateAdditionalOptions: boolean
}

const DefaultMinimumRadius = 15
const DefaultMaximumRadius = 50
export default class DefaultOptions extends React.PureComponent<DefaultOptionsProps, DefaultOptionsState> {
  state: DefaultOptionsState = {
    mpSitesOnly: false,
    mpSitesOnlyPortalAdmin: false,
    loadingOptions: false,
    loadingPrice: false,
    savingOptions: false,
    savingPrice: false,
    savingAdditionalOptions: false,
    updateAdditionalOptions: false
  }

  loadOptionsSubscription?: Subscription
  loadPriceSubscription?: Subscription
  saveOptionsSubscription?: Subscription
  savePriceSubscription?: Subscription

  componentDidMount() {
    this.setState({ loadingOptions: true })
    this.loadOptionsSubscription = ClinicSearchOptionsGetObservable().subscribe(
      data => this.setState({
        options: data,
        radius1: data.SearchRadius1 || DefaultMinimumRadius,
        radius2: data.SearchRadius2 || DefaultMaximumRadius,
        mpSitesOnly: data.mpSitesOnly || false,
        mpSitesOnlyPortalAdmin: data.mpSitesOnlyPortalAdmin || false,
        loadingOptions: false
      }),
      (error) => this.handleAPIError(error, { loadingOptions: false })
    )
    if (this.props.isPortal) {
      this.setState({ loadingPrice: true })
      this.loadPriceSubscription = ClinicSearchPriceGetObservable().subscribe(
        data => this.setState({ prices: data, loadingPrice: false }),
        (error) => this.handleAPIError(error, { loadingPrice: false })
      )
    }
  }

  componentWillUnmount() {
    this.loadOptionsSubscription && this.loadOptionsSubscription.unsubscribe()
    this.loadPriceSubscription && this.loadPriceSubscription.unsubscribe()
    this.saveOptionsSubscription && this.saveOptionsSubscription.unsubscribe()
    this.savePriceSubscription && this.savePriceSubscription.unsubscribe()
  }

  handleRadiusChange = (value: string, field: "radius1" | "radius2") => {
    const digits = value.replace(/\D/g, "")
    this.setState({ ...{}, [field]: digits ? Number(digits) : undefined })
  }

  handleChangePriceItemsList = (items: DraggableItemData[]) => {
    const { prices } = this.state
    if (!prices?.length) {
      return
    }
    const newPrices = items.map(item => {
      const price = prices.find(price => price.PriceTierID.toString() === item.id)
      return {...price, Exclude: item.exclude}
    }
    ).filter(item => !!item).map(item => ({ ...item } as ClinicSearchPrice))
    this.setState({ prices: newPrices })
  }

  handleAPIError = (error: Error, stateChanges: object) => {
    this.setState(stateChanges)
    toast.error(error)
  }

  handleUpdate = () => {
    this.setState({ savingOptions: true })
    this.saveOptionsSubscription = ClinicSearchOptionsSetObservable(this.state.radius1 || 0, this.state.radius2 || 0, this.state.mpSitesOnly).subscribe(
      (data) => toast.success(data),
      (error) => this.handleAPIError(error, {}),
      () => this.setState({ savingOptions: false })
    )
    if (this.props.isPortal) {
      if (!!this.state.prices) {
        this.savePriceSubscription = ClinicSearchPriceSetObservable(this.state.prices).subscribe(
          (data) => toast.success(data),
          (error) => this.handleAPIError(error, {}),
          () => this.setState({ savingOptions: false })
        )
      }

      this.setState(prev => ({ ...prev, updateAdditionalOptions: !prev.updateAdditionalOptions }))
    }
  }

  handleSaveAdditionalOptions = (saving: boolean) => {
    this.setState(prev => ({ ...prev, savingAdditionalOptions: saving }))
  }

  validationErrorsRadius1 = (): string[] => {
    const { radius1 } = this.state
    var errors: string[] = []
    const minValue = this.minRadius1Value()
    const maxValue = this.maxRadius1Value()
    if (!!radius1 && (radius1 < minValue || radius1 > maxValue)) {
      if (minValue === 1) {
        errors.push(`Minimum radius should be positive and not exceed ${maxValue} Miles`)
      } else {
        errors.push(`Minimum radius should be at least ${minValue} and no more than ${maxValue} Miles`)
      }
    }
    return errors
  }

  validationErrorsRadius2 = (): string[] => {
    const { radius1, radius2 } = this.state
    var errors: string[] = []
    const maxValue = this.maxRadius2Value()
    if (!!radius2 && !!radius1) {
      if (radius2 > maxValue) {
        errors.push(`Maximum radius should be greater than Minimum radius and not exceed ${maxValue} Miles`)
      }
      if (radius1 >= radius2) {
        errors.push("Maximum radius should be greater than Minimum radius")
      }
    }
    return errors
  }

  minRadius1Value = () => this.state.options?.ParentRadius1 || 1
  maxRadius1Value = () => this.state.options?.ParentRadius2 || this.state.options?.MaximumRadius || 50
  maxRadius2Value = () => this.state.options?.ParentRadius2 || this.state.options?.MaximumRadius || 50

  render() {
    const { prices, radius1, radius2, loadingOptions, loadingPrice, savingOptions, savingPrice, savingAdditionalOptions } = this.state

    const radius1Errors = this.validationErrorsRadius1()
    const radius2Errors = this.validationErrorsRadius2()
    const radius1Entered = radius1 ? radius1 > 0 : false
    const radius2Entered = radius2 ? radius2 > 0 : false
    const isPortal = this.props.isPortal

    return <>
      <Container fluid>
        <Row><Col className="pt-0">
          <div className="h5">Radius Defaults&nbsp;{loadingOptions && <Spinner animation="border" variant="secondary" size="sm" className="mb-1" />}</div>
        </Col></Row>
        {!loadingOptions && <Row>
          <Col lg={8}>
            <Container fluid className="p-0">
              <Row className="form-group">
                <Form.Label column xs={7} lg={3} htmlFor="searchRadius1" className="text-right">Minimum (Miles)</Form.Label>
                <Col xs={5} lg={1} className="p-0">
                  <Form.Control id="searchRadius1"
                    type="text"
                    maxLength={2}
                    isInvalid={!radius1Entered}
                    className="no-invalid-icon"
                    value={radius1?.toString() || ""}
                    onChange={e => this.handleRadiusChange(e.target.value, "radius1")} />
                </Col>
                <Form.Label column="sm" lg={7} htmlFor="searchRadius1" className="pl-1 pr-0 pt-0 pt-lg-2 text-right text-lg-left">
                  Integer between {this.minRadius1Value()} and {this.maxRadius1Value()} (inclusive)
                </Form.Label>
                <Col xs={12} className="text-right text-lg-center mt-0 pt-0 pr-0">
                  {radius1Errors.map((error, i) => <Form.Control.Feedback key={i} type="invalid" className="d-block">{error}</Form.Control.Feedback>)}
                </Col>
              </Row>
              <Row className="form-group">
                <Form.Label column xs={7} lg={3} htmlFor="searchRadius2" className="text-right">Maximum (Miles)</Form.Label>
                <Col xs={5} lg={1} className="p-0">
                  <Form.Control id="searchRadius2"
                    type="text"
                    maxLength={2}
                    isInvalid={!radius2Entered}
                    className="no-invalid-icon"
                    value={radius2?.toString() || ""}
                    onChange={e => this.handleRadiusChange(e.target.value, "radius2")} />
                </Col>
                <Form.Label column="sm" lg={7} htmlFor="searchRadius1" className="pl-1 pr-0 pt-0 pt-lg-2 text-right text-lg-left">
                  Integer greater than minimum and no more than {this.maxRadius2Value()}
                </Form.Label>
                <Col xs={12} className="text-right text-lg-center mt-0 pt-0 pr-0">
                  {radius2Errors.map((error, i) => <Form.Control.Feedback key={i} type="invalid" className="d-block">{error}</Form.Control.Feedback>)}
                </Col>
              </Row>
              <Row className="form-group">
                <Col>
                  <Checkbox
                    id="MPonly"
                    name="MPonly"
                    label="Marketplace Sites Only"
                    checked={this.state.mpSitesOnlyPortalAdmin || this.state.mpSitesOnly}
                    disabled={this.state.mpSitesOnlyPortalAdmin}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({mpSitesOnly: e.target.checked})} />
                </Col>
              </Row>
            </Container>
          </Col>
          {isPortal && <Col lg={4}><em className="small">When searching for clinic you can set a minimum and maximum radius default.</em></Col>}
        </Row>}
        {isPortal && <>
          <AdditionalOptions
            isPortal={isPortal}
            update={this.state.updateAdditionalOptions}
            setSaving={this.handleSaveAdditionalOptions} />
          {(loadingPrice || !!prices?.length) && <>
            <Row><Col>
              <div className="h5">Price Tier Display Order&nbsp;{loadingPrice && <Spinner animation="border" variant="secondary" size="sm" className="mb-1" />}</div>
            </Col></Row>
            {!loadingPrice && <Row>
              <Col lg={8}>
                <DndProvider backend={HTML5Backend}>
                  <DroppableContainer items={this.state.prices?.map(item => ({ id: item.PriceTierID.toString(), name: item.DisplayName, exclude: item.Exclude })) || []} onChangeItemsList={this.handleChangePriceItemsList} />
                </DndProvider>
              </Col>
              <Col lg={4}><em className="small">Drag and drop Tier to set the sort order for Tier to be displayed.</em></Col>
            </Row>}
          </>}
        </>}
        <Row className="buttons-block">
          <Button variant="secondary" onClick={this.props.onCancel} className="mr-1">Cancel</Button>
          <ActionButton variant="primary"
            disabled={radius1Errors.length + radius2Errors.length > 0 || !radius1Entered || !radius2Entered || loadingOptions || loadingPrice}
            onClick={this.handleUpdate}
            spin={savingOptions || savingPrice || savingAdditionalOptions}
            disableOnSpin>Update</ActionButton>
        </Row>
      </Container >
    </>
  }
}
